import { useRef } from 'react'
import { motion } from 'framer-motion'
import PageLayout from '../components/PageLayout'
import PageHero from '../components/PageHero'
import { useInView } from '../hooks/useInView'

const values = [
  {
    title: 'Trust is the product',
    body: 'Communities already have trust — we just give it a commerce layer. Every decision we make comes back to protecting the relationship between a licensor and the members who love it.',
  },
  {
    title: 'Licensors own what they build',
    body: 'The licensor should keep the revenue, the data, the members, and the relationships. Oak is built so you stay in control of how and where your products appear. No lock-in, no black boxes, no platform tax dressed up as a fee.',
  },
  {
    title: 'Meet communities where they are',
    body: 'We are platform agnostic by design. Integrate once and your products reach members consistently across every platform they already use — our own apps, social tools, or your own.',
  },
  {
    title: 'Boring infrastructure, interesting outcomes',
    body: 'Enrichment, distribution, payments, and signals should feel like plumbing — reliable, invisible, and working. The interesting part is the community you grow on top of it.',
  },
]

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.38, 0.005, 0.215, 1] as const },
  },
}

function StorySection() {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { threshold: 0.2 })

  return (
    <section ref={ref} className="rounded-section bg-white px-6 py-20 md:px-16 md:py-28">
      <motion.div
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
        variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
        className="mx-auto grid max-w-content grid-cols-1 gap-12 md:grid-cols-12 md:gap-16"
      >
        <motion.div variants={fadeUp} className="md:col-span-5">
          <span className="text-sm font-semibold uppercase tracking-widest text-gold-500">
            Our story
          </span>
          <h2 className="mt-4 font-serif text-[14vw] leading-[1] tracking-[-1.5px] md:text-[clamp(32px,5vw,56px)] text-oak-black">
            We started with the communities we already belonged to.
          </h2>
        </motion.div>
        <motion.div
          variants={fadeUp}
          className="space-y-5 text-[clamp(16px,1.5vw,19px)] leading-[1.6] tracking-[-0.2px] text-oak-black/70 md:col-span-7"
        >
          <p>
            Oak started with a simple observation: the most interesting commerce on the
            internet was happening inside group chats, forums, and social platforms — not
            on the homepages of the big marketplaces. Licensors had passionate communities,
            but their products lived as flat listings, disconnected from the conversation
            around them.
          </p>
          <p>
            We watched licensors rebuild the same experience over and over for every platform,
            while their best signals — what people loved, shared, and were talking about —
            went uncaptured. The data was inconsistent, the work was manual, and the
            community never quite came along for the ride.
          </p>
          <p>
            Oak is the platform that fixes that. Licensors integrate once, and our engine
            enriches every product with real context. From there, Oak distributes
            consistent experiences everywhere their community lives — on our own apps like
            Nanza or any third-party platform — and feeds the signals that matter back to
            the licensor.
          </p>
        </motion.div>
      </motion.div>
    </section>
  )
}

function ValuesSection() {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { threshold: 0.2 })

  return (
    <section ref={ref} className="rounded-section bg-white px-6 py-20 md:px-16 md:py-28">
      <div className="mx-auto max-w-content">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <span className="text-sm font-semibold uppercase tracking-widest text-gold-500">
            What we believe
          </span>
          <h2 className="mt-4 max-w-[720px] font-serif text-[14vw] leading-[1] tracking-[-1.5px] md:text-[clamp(32px,5vw,56px)] text-oak-black">
            Four principles behind every decision we make.
          </h2>
        </motion.div>
        <motion.div
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={{ visible: { transition: { staggerChildren: 0.12 } } }}
          className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8"
        >
          {values.map((v) => (
            <motion.div
              key={v.title}
              variants={fadeUp}
              className="rounded-3xl border border-oak-black/5 bg-oak-cream p-8 md:p-10"
            >
              <h3 className="text-[clamp(22px,2.5vw,28px)] font-medium leading-tight tracking-[-0.5px] text-oak-black">
                {v.title}
              </h3>
              <p className="mt-4 text-[clamp(15px,1.4vw,17px)] leading-[1.6] tracking-[-0.2px] text-oak-black/70">
                {v.body}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

function CTASection() {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { threshold: 0.3 })

  return (
    <section ref={ref} className="bg-oak-cream px-6 py-20 md:px-16 md:py-28">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8, ease: [0.38, 0.005, 0.215, 1] as const }}
        className="mx-auto max-w-3xl text-center"
      >
        <h2 className="font-serif text-[14vw] leading-[1] tracking-[-1.5px] md:text-[clamp(32px,5vw,56px)] text-oak-black">
          Building something your community would actually want?
        </h2>
        <p className="mx-auto mt-6 max-w-[480px] text-[clamp(16px,1.5vw,19px)] leading-[1.5] tracking-[-0.2px] text-oak-black/60">
          We would love to hear about it. Walk us through your community, and we will show you what Oak can do.
        </p>
        <a
          href="#demo"
          className="mt-10 inline-flex items-center gap-3 rounded-pill border border-oak-black/10 bg-oak-black px-7 py-4 text-[clamp(16px,1.5vw,20px)] font-medium tracking-[-0.4px] text-white transition-opacity hover:opacity-80"
        >
          Request a Demo
          <span className="h-8 w-8 rounded-full bg-oak-green" />
        </a>
      </motion.div>
    </section>
  )
}

export default function AboutPage() {
  return (
    <PageLayout>
      <PageHero
        eyebrow="About Oak"
        title="The social commerce platform built for communities."
        description="Oak helps licensors grow their community — enriching their products and reaching their members everywhere they already are."
      />
      <StorySection />
      <ValuesSection />
      <CTASection />
    </PageLayout>
  )
}
